import { InputAdornment, TextField, TextFieldProps } from '@material-ui/core';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

export type CompanyBasicFormFieldProps = TextFieldProps & {
  errorKeys?: Array<string>;
  selectOptions?: { label: string; value: string | number }[];
  hideEmptySelect?: boolean;
  icon?: React.ReactNode;
};

export const CompanyBasicFormField = ({
  errorKeys = [],
  selectOptions,
  hideEmptySelect = false,
  icon,
  ...props
}: CompanyBasicFormFieldProps) => {
  const selectProps = useMemo(
    () =>
      props.select
        ? {
            select: true,
            SelectProps: { ...props.SelectProps, native: true },
            children: (
              <>
                {!hideEmptySelect && <option value=""></option>}
                {selectOptions.map(({ label, value = label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </>
            ),
          }
        : null,
    [selectOptions, props.select, props.SelectProps, hideEmptySelect]
  );

  const iconProps = useMemo(
    () =>
      icon
        ? {
            InputProps: {
              ...props.InputProps,
              startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
            },
          }
        : null,
    [icon, props.InputProps]
  );

  const { register, errors } = useFormContext();

  const errorProps = useMemo(() => {
    const messages = [props.name]
      .concat(errorKeys)
      .filter(k => k && errors && errors[k])
      .map(k => `${errors[k]?.message}`);
    if (errors && messages.length) {
      return {
        error: true,
        helperText: messages.join(', '),
      };
    }
    return null;
  }, [errors, errorKeys]);
  return (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      {...props}
      {...selectProps}
      {...iconProps}
      {...errorProps}
      inputRef={register}
    />
  );
};

export const createTextAttr = (
  name: string,
  label: React.ReactNode,
  helperText: React.ReactNode,
  icon: React.ReactNode = null,
  errorKeys: string | Array<string> = []
): CompanyBasicFormFieldProps => ({
  label,
  helperText,
  icon,
  name,
  errorKeys: typeof errorKeys === 'string' ? [errorKeys] : errorKeys,
});

export const createDropDownAttr = (
  name: string,
  label: React.ReactNode,
  helperText: React.ReactNode,
  selectOptions: CompanyBasicFormFieldProps['selectOptions'],
  hideEmptySelect: boolean = false,
  errorKeys: string | Array<string> = []
): CompanyBasicFormFieldProps => ({
  label,
  helperText,
  name,
  selectOptions,
  hideEmptySelect,
  select: true,
  errorKeys: typeof errorKeys === 'string' ? [errorKeys] : errorKeys,
});
